import { AuthContext } from '../../providers/AuthProvider';
import { useContext, useState, useEffect } from 'react';
import { Loader } from './Loader';
import { config, useSettingsApi } from '../hooks/use-settings-api.hook';
import logger from '../../utils/logger';
import { isBookDemoPage } from '../../router/account-paths';

/**
 * Gates the app on the vendor's config having loaded.
 *
 * This used to also build the global MUI theme; the visual language now lives
 * entirely in the Tailwind tokens, so all that remains is the config fetch
 * (branding, language, colours for the visualizer preview) and the shell that
 * keeps the app at viewport size.
 */
const ThemeWrapper = ({ children }) => {
    const { isSuperAdmin, isAuthenticated } = useContext(AuthContext);
    /*
     * "Book a demo" is drawn in a frame over a prospect's demo page (see
     * isBookDemoPage): the shell paints nothing there, or its white covers
     * the shop the form is meant to sit on. A transparent html and body were
     * not enough while this full-screen shell was white (ARDE-292).
     */
    const [surface] = useState(() => (isBookDemoPage(window.location.pathname) ? 'bg-transparent' : 'bg-background'));
    const [isThemeReady, setIsThemeReady] = useState(false);
    const settingsApi = useSettingsApi();

    // Ensure config is loaded before rendering - react to auth changes
    useEffect(() => {
        const loadConfig = async () => {
            try {
                // Always reset theme ready state when dependencies change
                setIsThemeReady(false);

                // Force fetch settings to ensure config is populated
                // Always refetch when authenticated to ensure correct theme.
                // Not for super admins: they have no vendor config, the endpoint
                // answers 401, and every 401 sends the session through a refresh.
                if (isAuthenticated && !isSuperAdmin) {
                    await settingsApi.findConfig();
                }

                // Add a small delay to ensure UI updates with the theme
                setTimeout(() => {
                    setIsThemeReady(true);
                }, 100);
            } catch (error) {
                logger.error("Failed to load theme config:", error);
                // Even if there's an error, we still want to render with default theme
                setIsThemeReady(true);
            }
        };

        loadConfig();
    }, [isAuthenticated, isSuperAdmin]); // Re-run when auth state changes

    // If theme isn't ready, show a minimal loader
    if (!isThemeReady) {
        return (
            <div className={`flex h-screen w-screen items-center justify-center ${surface}`}>
                <Loader />
            </div>
        );
    }

    // Re-mount the shell when the branding or role context changes, exactly as
    // the old themed wrapper did.
    const themeKey = `${config?.secondaryColor || ''}${isAuthenticated ? '1' : '0'}${isSuperAdmin ? '1' : '0'}`;

    return (
        <div key={themeKey} className={`App relative h-screen w-screen overflow-hidden ${surface}`}>
            {children}
            <Loader />
        </div>
    );
};

export default ThemeWrapper;
